import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TableContainer, Paper, Table, TableRow, TableHead, TableCell, TableBody, CircularProgress, styled, Backdrop } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { fetchLoadingMessages, addLoadingMessage, updateLoadingMessage, deleteLoadingMessage } from 'app/services/LoadingMessageService';
import { ConfirmationDialog, PageLayout, SimpleCard } from 'app/components';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';

const TextField = styled(TextValidator)(() => ({
    width: "100%",
    marginBottom: "16px"
}));

const LoadingMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentMessage, setCurrentMessage] = useState({ title: { en: '', tr: '' } });
    const [isEdit, setIsEdit] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const loadMessages = async () => {
        try {
            setLoading(true);
            const messagesFromFirestore = await fetchLoadingMessages();
            setMessages(messagesFromFirestore);
        } catch (error) {
            console.error("Error loading messages:", error);
            enqueueSnackbar("An error occurred while loading messages.", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMessages();
    }, []);

    const handleSave = async () => {
        if (!currentMessage.title.en.trim() || !currentMessage.title.tr.trim()) {
            enqueueSnackbar("Message text cannot be empty in any language!", { variant: "error" });
            return;
        }
        try {
            setLoading(true);
            if (isEdit) {
                await updateLoadingMessage(currentMessage.id, currentMessage.title);
            } else {
                await addLoadingMessage(currentMessage.title);
            }
            await loadMessages();
            enqueueSnackbar("Loading Message saved successfully!", { variant: "success" });
            setOpenDialog(false);
        } catch (error) {
            console.error("Error saving message:", error);
            enqueueSnackbar("Failed to save message!", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (message) => {
        setCurrentMessage(message);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            await deleteLoadingMessage(currentMessage.id);
            await loadMessages();
            enqueueSnackbar("Loading Message deleted!", { variant: "success" });
        } catch (error) {
            console.error("Error deleting message:", error);
            enqueueSnackbar("Failed to delete message!", { variant: "error" });
        } finally {
            setLoading(false);
            setDeleteDialog(false);
        }
    };

    return (
        <PageLayout routeSegments={[{ name: "Loading Messages" }]}>
            <SimpleCard title="Loading Messages">
                <Box width="100%" overflow="auto">
                    <Button variant="contained" color="primary" onClick={() => { setCurrentMessage({ title: { en: '', tr: '' } }); setIsEdit(false); setOpenDialog(true); }} style={{ marginBottom: '16px', float: 'right' }}>
                        Add New Message
                    </Button>
                    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>English Title</TableCell>
                                    <TableCell>Turkish Title</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {messages.map((message) => (
                                    <TableRow key={message.id}>
                                        <TableCell>{message.id}</TableCell>
                                        <TableCell>{message.title.en}</TableCell>
                                        <TableCell>{message.title.tr}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => { setCurrentMessage(message); setIsEdit(true); setOpenDialog(true); }} color="primary">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(message)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                        <DialogTitle>{isEdit ? "Edit Message" : "Add New Message"}</DialogTitle>
                        <ValidatorForm onSubmit={handleSave}>
                            <DialogContent>
                                <TextField
                                    label="English Title"
                                    fullWidth
                                    value={currentMessage.title.en}
                                    onChange={(e) => setCurrentMessage({ ...currentMessage, title: { ...currentMessage.title, en: e.target.value } })}
                                    validators={["required"]}
                                    errorMessages={["This field is required"]}
                                />
                                <TextField
                                    label="Turkish Title"
                                    fullWidth
                                    value={currentMessage.title.tr}
                                    onChange={(e) => setCurrentMessage({ ...currentMessage, title: { ...currentMessage.title, tr: e.target.value } })}
                                    validators={["required"]}
                                    errorMessages={["This field is required"]}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                                <Button type="submit" color="primary">
                                    {isEdit ? "Update" : "Add"}
                                </Button>
                                <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                                    <CircularProgress color="inherit" />
                                </Backdrop>
                            </DialogActions>
                        </ValidatorForm>
                    </Dialog>
                    <ConfirmationDialog
                        title={"Confirm Delete"}
                        text={'Are you sure you want to delete this message?'}
                        open={deleteDialog}
                        onConfirmDialogClose={() => setDeleteDialog(false)}
                        onYesClick={handleConfirmDelete}
                    />
                </Box>
            </SimpleCard>
        </PageLayout>
    );
};

export default LoadingMessages;